import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import logger from '../../utils/logger';
import {
  fetchDashboardData,
  deleteInvestmentApi,
  fetchInvestmentByIdApi,
} from './dashboardApi';
import { pollPaymentStatusUtil, handleLogout as handleLogoutAction } from './useDashboardActions';

// URL contract: tab navigation only. NEVER include investment IDs, filter values,
// search queries, or any user data in the dashboard URL — they leak via browser
// history, Referer headers, and Sentry/PostHog event capture.
//
// The internal "dashboard" tab is exposed as "overview" in the URL because that
// matches the visible label ("Overview") and avoids the ambiguity of
// /dashboard/dashboard.
const URL_TO_TAB = {
  overview: 'dashboard',
  investments: 'investments',
  allocation: 'allocation',
  performance: 'performance',
  entities: 'entities',
  'estate-vault': 'estate-vault',
  add: 'add',
  sample: 'sample',
  sharing: 'sharing',
  faqs: 'faqs',
  privacy: 'privacy',
  settings: 'settings',
};
const TAB_TO_URL = Object.fromEntries(Object.entries(URL_TO_TAB).map(([u, t]) => [t, u]));

// localStorage key for the "last visited tab" memory. The key is intentionally
// generic (not user-scoped) because:
//   • the value is just a tab slug — no PII, no portfolio data;
//   • localStorage is already per-browser, per-origin;
//   • on a shared device the worst-case is "the next user lands on the wrong
//     tab", which is harmless since auth gates the actual data.
const LAST_TAB_STORAGE_KEY = 'mif:lastDashboardTab';

const readLastTab = () => {
  try {
    const slug = localStorage.getItem(LAST_TAB_STORAGE_KEY);
    return slug && URL_TO_TAB[slug] ? slug : null;
  } catch {
    return null;  // private mode / disabled storage
  }
};

export function useDashboard(user, subscriptionSuccess) {
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionModalDefaultType, setSubscriptionModalDefaultType] = useState('monthly');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showPrivacyWelcome, setShowPrivacyWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Tab state is mirrored to the URL so users can bookmark / share / refresh /
  // use browser back-forward. See URL_TO_TAB above for the URL contract.
  // URL is the single source of truth; setActiveTab navigates and the effect
  // below syncs state. This avoids double history-push from Radix Tabs firing
  // onValueChange on both focus and click for the same tab.
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTabState] = useState(() => URL_TO_TAB[urlTab] || 'dashboard');

  useEffect(() => {
    if (urlTab === undefined || !URL_TO_TAB[urlTab]) {
      // Land on the user's last-visited tab if we remember one, else Overview.
      // Deep links to specific tabs (when urlTab is valid) take precedence and
      // never hit this branch.
      const fallback = readLastTab() || 'overview';
      navigate(`/dashboard/${fallback}`, { replace: true });
      return;
    }
    const next = URL_TO_TAB[urlTab];
    setActiveTabState((prev) => (prev === next ? prev : next));
    // Remember this tab for next visit. Wrapped in try/catch elsewhere; the
    // setter is silent on failure (private mode, quota, etc.).
    try { localStorage.setItem(LAST_TAB_STORAGE_KEY, urlTab); } catch { /* noop */ }
  }, [urlTab, navigate]);

  // setActiveTab pushes a history entry only when the target URL differs from
  // the *live* pathname. Reading `window.location.pathname` directly (rather
  // than the memoised React Router `location.pathname`) is essential here:
  // Radix Tabs can fire onValueChange twice in the same micro-task (focus +
  // click), and a memoised React value would still hold the pre-click pathname
  // for the second call, causing a duplicate history push.
  const setActiveTab = useCallback((next) => {
    const slug = TAB_TO_URL[next];
    if (!slug) return;
    const target = `/dashboard/${slug}`;
    if (window.location.pathname === target) return;
    navigate(target);
  }, [navigate]);
  const [eligibility, setEligibility] = useState(null);
  const [investmentView, setInvestmentView] = useState('table');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingEligible, setSharingEligible] = useState(false);
  const [showInstitutionImportModal, setShowInstitutionImportModal] = useState(false);
  const [institutionImportSuccessMessage, setInstitutionImportSuccessMessage] = useState(null);
  const [showBatchAddModal, setShowBatchAddModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showQuickSetupModal, setShowQuickSetupModal] = useState(false);
  const [showDeepOnboardingModal, setShowDeepOnboardingModal] = useState(false);
  const [activeCoViewSession, setActiveCoViewSession] = useState(null);

  const isReadOnly = user?.auth_type === 'google' || user?.session_mode === 'quick_view';

  const handleReadOnlyAction = () => {
    if (user?.session_mode === 'quick_view') {
      toast.error("You're in Quick View. Switch to full access with your password to make changes.");
      return;
    }
    toast.error('For security reasons, editing requires email verification and 2FA.');
  };

  const pollPaymentStatus = (sessionId) => pollPaymentStatusUtil(sessionId, loadDashboardData, setShowSuccessModal);

  // Idempotency ref — StrictMode double-invokes mount effects in dev; without
  // this guard the second pass reads the just-written `hasSeenOnboarding`
  // value and would fire the privacy tour on a brand-new user.
  const onboardingBoostrapDoneRef = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (subscriptionSuccess) {
      setShowSuccessModal(true);
    }
    loadDashboardData();
    if (onboardingBoostrapDoneRef.current) return; // no-op on the StrictMode second pass
    onboardingBoostrapDoneRef.current = true;
    const hadSeenOnboarding = localStorage.getItem('hasSeenOnboarding') === 'true';
    // Privacy Welcome Slides — fire once for existing members only. A brand-new
    // signup (no `hasSeenOnboarding`) sees the classic WelcomeModal; we mark
    // the privacy tour as "seen" for them too so they don't get a double-modal
    // stack on first mount. They can still Replay the tour from the Your Privacy tab.
    if (!hadSeenOnboarding) {
      localStorage.setItem('hasSeenOnboarding', 'true');
      try { localStorage.setItem('mif:privacyWelcomeSeen', '1'); } catch { /* private mode */ }
      return;
    }
    try {
      const seenPrivacy = localStorage.getItem('mif:privacyWelcomeSeen');
      if (!seenPrivacy) {
        setTimeout(() => setShowPrivacyWelcome(true), 600);
      }
    } catch { /* private mode */ }
  }, [subscriptionSuccess]);

  useEffect(() => {
    const handler = (event) => {
      const { defaultType } = event.detail || {};
      setSubscriptionModalDefaultType(defaultType || 'monthly');
      setShowSubscriptionModal(true);
    };
    window.addEventListener('openSubscriptionModal', handler);
    return () => window.removeEventListener('openSubscriptionModal', handler);
  }, []);

  // Phase 4a Chunk 2: newly-signed-up Continuity users land here with the
  // ``open_subscription_on_dashboard`` flag in sessionStorage (set by
  // CreateAccountModal). We auto-pop the payment modal on mount and clear
  // the flag so a subsequent visit doesn't nag them again.
  useEffect(() => {
    if (sessionStorage.getItem('open_subscription_on_dashboard') === '1') {
      sessionStorage.removeItem('open_subscription_on_dashboard');
      // Small delay so the dashboard has settled and doesn't clash with the
      // onboarding welcome modal on the same mount cycle.
      setTimeout(() => {
        setSubscriptionModalDefaultType('monthly');
        setShowSubscriptionModal(true);
      }, 400);
    }
  }, []);

  // Cross-tab jump requests from anywhere in the tree land on
  // ``useCrossTabJump`` (mounted at the Dashboard level). No legacy
  // 'setDashboardTab' listener here any more — consolidated in Session 260.

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const data = await fetchDashboardData('real');
      setPortfolioSummary(data.summary);
      setInvestments(data.investments);
      const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
      if (!hasSeenOnboarding && (!data.investments || data.investments.length === 0)) {
        setShowWelcomeModal(true);
      }
      setEligibility(data.eligibility);
      setSharingEligible(data.sharingEligible);
    } catch (err) {
      logger.error(err);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = handleLogoutAction;

  const handleStartTour = () => { setShowWelcomeModal(false); setRunTour(true); };

  const handleCloseTour = () => {
    setRunTour(false);
    toast.success('Tour complete! Redirecting to home...', { duration: 2000 });
    setTimeout(() => { window.location.href = '/'; }, 800);
  };

  const handleRestartTour = () => {
    localStorage.removeItem('hasSeenOnboarding');
    setShowWelcomeModal(true);
    toast.success("Welcome back! Let's take the tour again.");
  };

  const handleRestartPrivacyWelcome = () => {
    try { localStorage.removeItem('mif:privacyWelcomeSeen'); } catch { /* noop */ }
    setShowPrivacyWelcome(true);
  };

  const handleEditInvestment = (investment) => {
    setSelectedInvestment(investment);
    setShowAddModal(true);
  };

  const handleEditInvestmentById = async (investmentId) => {
    try {
      const investment = await fetchInvestmentByIdApi(investmentId);
      setSelectedInvestment(investment);
      setShowAddModal(true);
      setActiveTab('add');
    } catch (err) {
      logger.error(err);
      toast.error('Failed to load investment');
    }
  };

  const handleDeleteInvestment = async (investmentId) => {
    if (!window.confirm('Are you sure you want to delete this investment?')) return;
    try {
      await deleteInvestmentApi(investmentId);
      toast.success('Investment deleted successfully');
      loadDashboardData();
    } catch (err) {
      logger.error(err);
      toast.error('Failed to delete investment');
    }
  };

  const handleAddInvestmentSuccess = () => {
    loadDashboardData();
    setShowAddModal(false);
    setSelectedInvestment(null);
  };

  return {
    portfolioSummary, investments, isLoading, eligibility, sharingEligible,
    activeTab, setActiveTab, investmentView, setInvestmentView,
    showAddModal, setShowAddModal, selectedInvestment, setSelectedInvestment,
    showSuccessModal, setShowSuccessModal,
    showSubscriptionModal, setShowSubscriptionModal,
    subscriptionModalDefaultType, setSubscriptionModalDefaultType,
    showWelcomeModal, setShowWelcomeModal, runTour,
    showPrivacyWelcome, setShowPrivacyWelcome,
    handleRestartPrivacyWelcome,
    showShareModal, setShowShareModal,
    showInstitutionImportModal, setShowInstitutionImportModal,
    institutionImportSuccessMessage, setInstitutionImportSuccessMessage,
    showBatchAddModal, setShowBatchAddModal,
    showOnboardingModal, setShowOnboardingModal,
    showQuickSetupModal, setShowQuickSetupModal,
    showDeepOnboardingModal, setShowDeepOnboardingModal,
    activeCoViewSession, setActiveCoViewSession,
    isReadOnly, handleReadOnlyAction,
    loadDashboardData, handleLogout,
    handleStartTour, handleCloseTour, handleRestartTour,
    handleEditInvestment, handleEditInvestmentById, handleDeleteInvestment,
    handleAddInvestmentSuccess,
  };
}
